/**
 * OllieTot Analytics Tracker
 * Enhanced JavaScript client for tracking user interactions on Shopify store
 * Includes: Device fingerprinting, Ad tracking (GCLID/FBCLID), Return visitors,
 * Cart/Checkout funnel, Form tracking, Search tracking, Performance metrics
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    debug: false,  // Set to true to log events to console instead of sending to server
    // apiEndpoint: 'https://your-domain.com/api/track/',  // Update with your AWS EC2 domain
    // conversionEndpoint: 'https://your-domain.com/api/convert/',

    // apiEndpoint: 'http://localhost:8000/api/track/',  // Update with your AWS EC2 domain
    // conversionEndpoint: 'http://localhost:8000/api/convert/',
    // apiKey: 'dev-key-change-in-production',  // IMPORTANT: Change this in production

    // First-party same-origin collector. The storefront beacons to /apps/ot/*
    // (the store's own domain — unblockable by content blockers, no ITP), and
    // Shopify's App Proxy forwards it to the signed /shopify-proxy/* backend.
    // Relative on purpose so it always resolves to the storefront origin.
    // (Direct https://perf.ollietot.com/api/track|convert still exist and work,
    // kept as the pre-proxy transport for reference.)
    apiEndpoint: '/apps/ot/collect',
    conversionEndpoint: '/apps/ot/convert',
    apiKey: 'VSeiAX1rdVEJdMDykV1pDlZ0nNC59vHN54HRgBwO+J4=',  // vestigial on the proxy path (signature-authed) but harmless
    sessionIdKey: 'ollietot_session_id',
    visitorIdKey: 'ollietot_visitor_id',
    firstTouchKey: 'ollietot_first_touch',
    visitCountKey: 'ollietot_visit_count',
    lastVisitKey: 'ollietot_last_visit',
    cartTaggedKey: 'ollietot_cart_tagged',
    // Two clocks, deliberately. sessionTtlSeconds bounds ONE visit — a longer
    // window would merge January and June into a single "session" and make
    // every per-session metric (bounce, pages/session, time on site, the
    // funnel, the Meta EngagedVisit trigger) meaningless. visitorTtlSeconds is
    // the durable identity that survives across visits, which is what actually
    // needs a long life.
    sessionTtlSeconds: 30 * 60,          // 30-min sliding session window
    visitorTtlSeconds: 365 * 24 * 60 * 60,  // 12-month first-party visitor cookie
    cartSessionAttribute: '_ot_session_id',
    batchSize: 10,
    batchTimeout: 5000,  // 5 seconds
    activeTimeInterval: 1000,  // Check activity every second
    idleThreshold: 30000,  // 30 seconds of inactivity = idle
    heartbeatInterval: 30000,  // Send heartbeat every 30 seconds
    hoverMinMs: 400,  // Minimum dwell before a hover counts
    imageVisibleThreshold: 0.3,  // Fraction of an image on screen to count as visible
    imageMinVisibleMs: 300,  // Minimum visibility before an impression fires
  };

  // State
  let eventQueue = [];
  let batchTimer = null;
  let pageLoadTime = Date.now();
  let lastActivityTime = Date.now();
  let isUserActive = true;
  let activeTimeSeconds = 0;
  let pageSequence = 0;
  let currentFormId = null;
  let formStartTime = null;
  let lastCheckoutStart = 0;
  const CHECKOUT_START_DEBOUNCE_MS = 3000;
  // The URL as of the page_view, kept so the deferred performance pass can
  // address the PageView row it belongs to. Not read live at load time: the
  // theme's in-page CTAs pushState a #fragment onto the URL, and the server
  // matches a PageView by exact url, so a tap before window.load would send
  // the perf marks to a row that does not exist.
  let pageViewUrl = '';
  // The post-load performance marks, held so they can ride the next couple of
  // heartbeats as well as their own.
  //
  // They are sent once, on one queued (fetch) batch — while trackPageView
  // goes out over sendBeacon, which the browser queues at low priority with
  // no ordering guarantee against a later fetch. If the heartbeat commits
  // first there is no PageView row yet, PageView.DoesNotExist fires, and
  // views.py swallows it; the server backfill is write-once, so those four
  // columns are then gone for that session with no second chance. Every other
  // backfilled field self-heals because the 30s heartbeat re-sends it. These
  // did not. Repeating them costs four integers on two requests, and the
  // write-once backfill makes the repeat a no-op once one has landed.
  let pendingPerfMetrics = null;
  let perfResendsLeft = 0;
  const PERF_RESENDS = 2;
  function withPendingPerf(payload) {
    if (!pendingPerfMetrics || perfResendsLeft <= 0) return payload;
    perfResendsLeft--;
    const merged = Object.assign({}, pendingPerfMetrics, payload);
    if (perfResendsLeft <= 0) pendingPerfMetrics = null;
    return merged;
  }

  // Utility Functions
  function generateSessionId() {
    return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  function generateVisitorId() {
    return 'vis_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  function readCookie(name) {
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
    const match = document.cookie.match('(?:^|; )' + escaped + '=([^;]*)');
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, maxAgeSeconds) {
    let cookie = name + '=' + encodeURIComponent(value)
      + '; path=/; max-age=' + maxAgeSeconds + '; SameSite=Lax';
    if (location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  // The session id lives in a first-party cookie, not sessionStorage: the
  // Instagram/Facebook in-app browser (iOS WKWebView) spins up a fresh
  // sessionStorage per navigation, which was splitting one visit (home -> PDP)
  // into separate sessions. A cookie survives that. Sliding 30-min expiry;
  // refreshed on every access.
  function getSessionId() {
    let sessionId = readCookie(config.sessionIdKey);
    if (!sessionId) {
      // Adopt an existing per-tab id from the old scheme so a visit already in
      // progress when this deploys isn't split.
      try { sessionId = sessionStorage.getItem(config.sessionIdKey); } catch (e) {}
      if (!sessionId) sessionId = generateSessionId();
    }
    writeCookie(config.sessionIdKey, sessionId, config.sessionTtlSeconds);
    try { sessionStorage.setItem(config.sessionIdKey, sessionId); } catch (e) {}
    return sessionId;
  }

  // Durable identity across visits, in a 12-month first-party cookie refreshed
  // on every page so the year is measured from the last visit, not the first.
  //
  // Caveat worth knowing when reading the numbers: Safari (and every iOS
  // browser, including the Instagram/Facebook in-app WebView) caps cookies
  // written from JavaScript at 7 days under ITP, so those visitors get a new
  // id after a week away no matter what max-age we ask for. Chrome, Edge,
  // Firefox and Android WebViews honour the full year. The App Proxy cannot
  // help here — Shopify strips Set-Cookie from proxied responses — so the
  // localStorage mirror below is the only extra durability available, and it
  // is subject to the same 7-day cap on iOS.
  function getVisitorId() {
    let visitorId = readCookie(config.visitorIdKey);
    if (!visitorId) {
      try { visitorId = localStorage.getItem(config.visitorIdKey); } catch (e) {}
    }
    if (!visitorId) visitorId = generateVisitorId();
    writeCookie(config.visitorIdKey, visitorId, config.visitorTtlSeconds);
    try { localStorage.setItem(config.visitorIdKey, visitorId); } catch (e) {}
    return visitorId;
  }

  // Which deployed version of the storefront this page is. Set by the theme
  // (Online Store -> Customize -> Theme settings -> Analytics); blank until
  // one is configured, which reads as "unversioned" server-side rather than
  // as an error.
  function getSiteVersion() {
    let version = window.OLLIETOT_SITE_VERSION;
    if (!version) {
      const meta = document.querySelector('meta[name="ot:site-version"]');
      version = meta ? meta.getAttribute('content') : '';
    }
    return (version || '').toString().trim().slice(0, 64);
  }

  // First-touch attribution: capture the ad's UTM / click-ids from the FIRST
  // page of the visit and persist them for the whole session. Later pages
  // (especially after an in-app-browser navigation) have no query string, so
  // reading live from window.location would send blanks. Falls back to parsing
  // the referrer, which on a fragmented PDP hit still holds the home ?utm_...
  function getFirstTouchAttribution() {
    const sid = getSessionId();
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(config.firstTouchKey) || 'null'); }
    catch (e) { stored = null; }
    if (stored && stored.session === sid) return stored.attribution;

    const url = new URLSearchParams(window.location.search);
    let ref;
    try { ref = new URLSearchParams(new URL(document.referrer).search); }
    catch (e) { ref = new URLSearchParams(''); }
    const pick = (key) => url.get(key) || ref.get(key) || '';
    const attribution = {
      utm_source: pick('utm_source'),
      utm_medium: pick('utm_medium'),
      utm_campaign: pick('utm_campaign'),
      utm_content: pick('utm_content'),
      utm_term: pick('utm_term'),
      gclid: pick('gclid'),
      fbclid: pick('fbclid'),
      // OpenAI (ChatGPT) ads click id. Same first-touch treatment as the
      // others: an in-app browser drops the query string on the next hop.
      oppref: pick('oppref'),
    };
    // Persist the first non-empty capture for this session; don't overwrite a
    // real first-touch with a later blank page.
    if (Object.values(attribution).some(Boolean)) {
      try {
        localStorage.setItem(config.firstTouchKey,
          JSON.stringify({ session: sid, attribution }));
      } catch (e) {}
    }
    return (stored && stored.attribution) || attribution;
  }

  function getUTMParams() {
    const a = getFirstTouchAttribution();
    return {
      utm_source: a.utm_source || '',
      utm_medium: a.utm_medium || '',
      utm_campaign: a.utm_campaign || '',
      utm_content: a.utm_content || '',
      utm_term: a.utm_term || '',
    };
  }

  function getAdPlatformIds() {
    const a = getFirstTouchAttribution();
    return {
      gclid: a.gclid || '',  // Google Click ID
      fbclid: a.fbclid || '',  // Facebook Click ID
      oppref: a.oppref || '',  // OpenAI (ChatGPT) ads click ID
    };
  }

  function getDeviceFingerprint() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      color_depth: window.screen.colorDepth,
      pixel_ratio: window.devicePixelRatio,
      user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      is_touch_device: navigator.maxTouchPoints > 0 || 'ontouchstart' in window,
      connection_type: connection ? connection.effectiveType : '',
    };
  }

  // Computed once per page load; incrementing inside every payload build
  // inflated visit counts by one per tracked event.
  let returnVisitorData = null;

  function getReturnVisitorData() {
    if (returnVisitorData) return returnVisitorData;

    const visitCount = parseInt(localStorage.getItem(config.visitCountKey) || '0', 10);
    const lastVisit = localStorage.getItem(config.lastVisitKey);

    let daysSinceLastVisit = null;
    if (lastVisit) {
      const daysDiff = Math.floor((Date.now() - parseInt(lastVisit, 10)) / (1000 * 60 * 60 * 24));
      daysSinceLastVisit = daysDiff;
    }

    // Count a new visit only once per session. The marker lives in localStorage
    // keyed to the durable (cookie) session id, so navigating between pages in
    // the in-app browser doesn't re-count the same visit.
    const countedFor = localStorage.getItem(config.visitCountKey + '_counted');
    let previousVisits = visitCount;
    if (countedFor !== getSessionId()) {
      localStorage.setItem(config.visitCountKey, (visitCount + 1).toString());
      localStorage.setItem(config.lastVisitKey, Date.now().toString());
      localStorage.setItem(config.visitCountKey + '_counted', getSessionId());
    } else {
      // Already counted this session; the stored total includes it.
      previousVisits = Math.max(visitCount - 1, 0);
    }

    returnVisitorData = {
      is_returning_visitor: previousVisits > 0,
      previous_visit_count: previousVisits,
      days_since_last_visit: daysSinceLastVisit,
    };
    return returnVisitorData;
  }

  // Milliseconds after navigation start at which this page FIRST became
  // hidden — 0 if it was already hidden when the tracker booted (a background
  // tab restored at startup, a link opened in a new tab), Infinity if it has
  // been visible throughout.
  //
  // Every metric below is a wall-clock delta from navigationStart, and a
  // backgrounded tab keeps that clock running while the browser throttles the
  // work being measured. That is where the 6.6-million-ms first paints came
  // from. A mark recorded after the page went away does not describe how fast
  // the page was, so it is reported as null rather than as a number that will
  // be averaged. (Same rule the web-vitals library applies to FCP/LCP.)
  // Seeded from three sources, in order of how far back each can see:
  //   1. window.__otFirstHidden, stamped by a two-line inline script at the
  //      top of theme.liquid's <head>. This tracker is a DEFERRED script —
  //      the same fact that made page_load_time 100% NULL — so on its own it
  //      cannot see the window it most needs to: a tab opened in the
  //      background and foregrounded again before this file evaluates reads
  //      'visible', sets Infinity, and passes the inflated paint time
  //      straight through. The head stamp runs before all of that.
  //   2. performance.getEntriesByType('visibility-state'), the same timeline
  //      web-vitals reads. Chromium 115+ only, but exact where present.
  //   3. document.visibilityState as of now — the original best-effort read.
  // Where neither of the first two is available (Safari and WKWebView on a
  // page without the head stamp) this stays best effort, so FCP is a median
  // to be read, never a mean.
  let firstHiddenTime = document.visibilityState === 'hidden' ? 0 : Infinity;
  if (typeof window.__otFirstHidden === 'number') {
    firstHiddenTime = Math.min(firstHiddenTime, window.__otFirstHidden);
  }
  try {
    if (window.performance && window.performance.getEntriesByType) {
      const hiddenEntry = window.performance
        .getEntriesByType('visibility-state')
        .find(entry => entry.name === 'hidden');
      if (hiddenEntry) {
        firstHiddenTime = Math.min(firstHiddenTime, hiddenEntry.startTime);
      }
    }
  } catch (err) {
    /* entry type unsupported — keep the best-effort value above */
  }
  function noteHidden() {
    if (document.visibilityState === 'hidden' && firstHiddenTime === Infinity) {
      firstHiddenTime = (window.performance && window.performance.now)
        ? window.performance.now() : 0;
    }
  }
  document.addEventListener('visibilitychange', noteHidden, true);
  window.addEventListener('pagehide', noteHidden, true);

  function getPerformanceMetrics() {
    if (!window.performance || !window.performance.timing) {
      return {};
    }

    const timing = window.performance.timing;
    const paint = window.performance.getEntriesByType('paint');

    // A mark is only reported if it fired while the page was still visible.
    // `mark` is ms since navigation start, the same origin firstHiddenTime is
    // measured in.
    const whileVisible = (mark) => (mark > 0 && mark < firstHiddenTime) ? mark : null;

    let firstContentfulPaint = null;
    if (paint && paint.length > 0) {
      const fcp = paint.find(entry => entry.name === 'first-contentful-paint');
      firstContentfulPaint = fcp ? whileVisible(Math.round(fcp.startTime)) : null;
    }

    // The page view fires immediately (before window.load), so these timing
    // marks are often still 0. Guard EACH of them: a raw
    // `0 - navigationStart` is a large negative number, not a valid metric —
    // report null until the underlying event has actually fired. The load and
    // DOMContentLoaded marks are re-read after window.load and sent on a
    // second event; see trackPagePerformance.
    return {
      page_load_time: timing.loadEventEnd > 0
        ? whileVisible(timing.loadEventEnd - timing.navigationStart) : null,
      dom_content_loaded: timing.domContentLoadedEventEnd > 0
        ? whileVisible(timing.domContentLoadedEventEnd - timing.navigationStart) : null,
      time_to_interactive: timing.domInteractive > 0
        ? whileVisible(timing.domInteractive - timing.navigationStart) : null,
      first_contentful_paint: firstContentfulPaint,
    };
  }

  function getSessionData() {
    return {
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      site_version: getSiteVersion(),
      ...getUTMParams(),
      ...getAdPlatformIds(),
      // Meta Pixel cookies, when the pixel is installed: _fbp (browser id)
      // and _fbc (pixel-formatted click id). Sent so the server-side
      // Conversions API events carry the same identity the pixel would.
      fbp: readCookie('_fbp') || '',
      fbc: readCookie('_fbc') || '',
      // The OpenAI pixel's first-party browser id, its equivalent of _fbp.
      // Its click id (oppref) rides along in getAdPlatformIds above.
      obref: readCookie('__obref') || '',
      ...getDeviceFingerprint(),
      ...getReturnVisitorData(),
      referrer: document.referrer || '',
      landing_page: window.location.href,
      user_agent: navigator.userAgent,
    };
  }

  // ---- OpenAI (ChatGPT) ads measurement pixel -------------------------------
  //
  // The pixel is initialised in the theme's head (snippets/openai-pixel.liquid),
  // which also fires page_viewed and, on a product page, contents_viewed — those
  // two need Liquid's product data and must not wait on this deferred script.
  // The funnel events below are mirrored from the tracking calls the theme
  // already makes, so there is one call site per event rather than a second set
  // of listeners that could drift from the first.
  //
  // No-ops when the pixel is absent (pixel id blanked in theme settings, script
  // blocked, debug mode) — nothing on the page may depend on it loading.
  function oaiMeasure(eventName, data, eventId) {
    if (config.debug) {
      console.log('[OllieTot][oaiq]', eventName, data, eventId);
      return;
    }
    if (typeof window.oaiq !== 'function') return;
    try {
      window.oaiq('measure', eventName, data, eventId ? { event_id: eventId } : {});
    } catch (e) {}
  }

  // Dollars -> integer minor units. The OpenAI API takes amounts in cents and
  // rejects non-integers; the theme reports prices in dollars.
  function oaiAmount(price) {
    const cents = Math.round((Number(price) || 0) * 100);
    return cents > 0 ? cents : null;
  }

  // Send a batch of events to the server in a single request:
  // { ...session fields, events: [{event_type, ...}, ...] }
  function sendBatch(events, useBeacon) {
    if (!events.length) return;

    const payload = {
      ...getSessionData(),
      events: events,
    };

    // Debug mode - log to console instead of sending
    if (config.debug) {
      console.group('🎯 OllieTot Analytics Batch:', events.map(e => e.event_type).join(', '));
      console.log('Events:', events);
      console.log('Full Payload:', payload);
      console.log('Session ID:', payload.session_id);
      console.log('Note: IP address will be captured server-side from request');
      console.groupEnd();
      return;
    }

    // During page unload, fetch with a custom header needs a CORS preflight
    // that browsers refuse to run — the batch silently vanishes. sendBeacon
    // with a text/plain body is a "simple" request (no preflight) and is the
    // only transport guaranteed to survive unload; the API key travels as a
    // query param since beacons cannot set headers.
    if (useBeacon && navigator.sendBeacon) {
      const beaconUrl = config.apiEndpoint + '?k=' + encodeURIComponent(config.apiKey);
      // A plain string, NOT a Blob: some Android in-app browsers (Facebook's
      // FB_IAB) serialize a Blob beacon body to the literal "[object Blob]",
      // corrupting the unload flush. A string body ships as
      // text/plain;charset=UTF-8 — still preflight-free, parsed server-side
      // by the same text/plain JSON parser.
      if (navigator.sendBeacon(beaconUrl, JSON.stringify(payload))) return;
      // Beacon refused (queue full) — fall through to fetch as a last resort.
    }

    fetch(config.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Analytics-Key': config.apiKey,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(result => {
        if (console.debug) {
          console.debug('Analytics tracked:', result);
        }
      })
      .catch(err => console.error('Analytics error:', err));
  }

  function flushQueue(useBeacon) {
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
    if (eventQueue.length === 0) return;
    sendBatch(eventQueue.splice(0, eventQueue.length), useBeacon);
  }

  // Server-side max lengths; overlong values would invalidate the event, so
  // trim rather than lose it.
  const FIELD_LIMITS = {
    element_id: 255,
    element_class: 255,
    element_text: 2000,
    page_title: 500,
    page_url: 2048,
    image_url: 2048,
  };

  function sanitizeEvent(eventData) {
    Object.keys(FIELD_LIMITS).forEach(field => {
      if (typeof eventData[field] === 'string' && eventData[field].length > FIELD_LIMITS[field]) {
        eventData[field] = eventData[field].substring(0, FIELD_LIMITS[field]);
      }
    });
    return eventData;
  }

  // Every event carries the page context as of the moment it happened;
  // explicit values from the tracking call win over the snapshot.
  function enrichEvent(eventData) {
    // Stamped per event, not only per batch: a version bump between two
    // flushes must not retroactively relabel events from the older page.
    if (eventData.site_version === undefined) eventData.site_version = getSiteVersion();
    if (eventData.page_url === undefined) eventData.page_url = window.location.href;
    if (eventData.page_title === undefined) eventData.page_title = document.title;
    if (eventData.scroll_depth === undefined) eventData.scroll_depth = getCurrentScrollDepth();
    if (eventData.active_time === undefined) eventData.active_time = activeTimeSeconds;
    if (eventData.time_on_page === undefined) {
      eventData.time_on_page = Math.floor((Date.now() - pageLoadTime) / 1000);
    }
    return eventData;
  }

  function queueEvent(eventData) {
    eventQueue.push(sanitizeEvent(enrichEvent(eventData)));

    if (eventQueue.length >= config.batchSize) {
      flushQueue();
    } else if (!batchTimer) {
      batchTimer = setTimeout(flushQueue, config.batchTimeout);
    }
  }

  // Queue and flush immediately — for events that must not wait out the
  // batch window (add to cart, checkout, purchase). Pass useBeacon=true for
  // events that must survive an imminent page teardown (the landing page view
  // on a fast bounce): sendBeacon is the only transport that survives the
  // WKWebView being torn down mid-navigation; keepalive fetch is unreliable
  // there. sendBatch falls back to fetch if the beacon is refused.
  function sendEvent(eventData, useBeacon) {
    eventQueue.push(sanitizeEvent(enrichEvent(eventData)));
    flushQueue(useBeacon);
  }

  // Active time tracking
  function trackActiveTime() {
    setInterval(() => {
      const now = Date.now();
      if (now - lastActivityTime < config.idleThreshold) {
        if (!isUserActive) {
          isUserActive = true;
        }
        activeTimeSeconds++;
      } else if (isUserActive) {
        isUserActive = false;
      }
    }, config.activeTimeInterval);
  }

  function recordActivity() {
    lastActivityTime = Date.now();
    isUserActive = true;
  }

  // `computed` is an OPTIONAL, already-obtained getComputedStyle result. The
  // hero paints its photograph through a CSS custom property —
  // style="--hero-wide: url(...)" with the stylesheet doing
  // `background-image: var(--hero-wide)` — so its INLINE backgroundImage is
  // empty and the hero, the single largest and most-seen image on the site,
  // emitted zero impressions. The resolved value is only available from
  // getComputedStyle, which also picks the right one of the wide/portrait
  // pair for the viewport that actually rendered.
  //
  // getComputedStyle forces style resolution, so it must never run per
  // element per observer tick. It doesn't: the only caller that passes
  // `computed` is endImageEpisode, which already computes the style to check
  // opacity — this reuses that object and adds no work at all. Callers
  // without one (resolveElementId, which reaches here only for <img>) get the
  // old inline-only behaviour.
  function imageUrlOf(el, computed) {
    if (el.tagName === 'IMG') return el.currentSrc || el.src || '';
    let bg = (el.style && el.style.backgroundImage) || '';
    if (!bg || bg === 'none') bg = (computed && computed.backgroundImage) || '';
    if (!bg || bg === 'none') return '';
    const match = bg.match(/url\(["']?([^"')]+)["']?\)/);
    return match ? match[1] : '';
  }

  function imageSlugOf(url) {
    try {
      const path = new URL(url, window.location.href).pathname;
      return decodeURIComponent(path.split('/').pop() || '')
        .replace(/\.[a-z0-9]+$/i, '')            // extension
        .replace(/_(?:\d+x\d*|x\d+)$/i, '');     // Shopify size suffix (_1400x, _800x600)
    } catch (err) {
      return '';
    }
  }

  // Prefer a descriptive analytics id declared in the markup (or composed by
  // theme JS) over raw DOM ids, which are usually blank on styled controls.
  // Untagged images identify themselves by filename slug, prefixed with any
  // tagged ancestor for context (e.g. photo-rail:paloma:...:img:ot-paloma-rail-03).
  function resolveElementId(el) {
    if (!el || !el.closest) return '';
    const tagged = el.closest('[data-analytics-id]');
    if (el.tagName === 'IMG' && (!tagged || tagged !== el)) {
      const slug = imageSlugOf(imageUrlOf(el));
      if (slug) {
        const prefix = tagged ? tagged.getAttribute('data-analytics-id') + ':' : '';
        return prefix + 'img:' + slug;
      }
    }
    if (tagged) return tagged.getAttribute('data-analytics-id');
    return el.id || '';
  }

  // Get current scroll position
  function getCurrentScrollDepth() {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    return Math.max(0, Math.min(100, scrollPercent || 0));
  }

  // Heartbeat tracking
  function startHeartbeat() {
    setInterval(() => {
      // Only send heartbeat if page is visible
      if (document.visibilityState !== 'visible') {
        return;
      }

      // Get current state
      const currentScrollDepth = getCurrentScrollDepth();
      const timeOnPage = Math.floor((Date.now() - pageLoadTime) / 1000);

      // Send heartbeat event with current state
      queueEvent(withPendingPerf({
        event_type: 'heartbeat',
        page_url: window.location.href,
        page_title: document.title,
        scroll_depth: currentScrollDepth,
        active_time: activeTimeSeconds,
        time_on_page: timeOnPage,
      }));
    }, config.heartbeatInterval);
  }

  // Tracking Functions
  const OllieTotAnalytics = {
    // Stamp the Shopify cart with this session id so the orders/paid webhook
    // can attribute the purchase (theme JS never runs on hosted checkout).
    // No-op outside Shopify or when the cart is already tagged.
    tagCart() {
      if (localStorage.getItem(config.cartTaggedKey) === getSessionId()) {
        return Promise.resolve();
      }
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const attributes = {};
      attributes[config.cartSessionAttribute] = getSessionId();
      return fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      })
        .then(response => {
          if (response.ok) {
            localStorage.setItem(config.cartTaggedKey, getSessionId());
          }
        })
        .catch(() => {});
    },

    // Track page view with performance metrics. Sent via sendBeacon so the
    // landing hit survives a fast bounce in the in-app browser (the session
    // is created even if the user taps away before anything else fires).
    trackPageView() {
      const perfMetrics = getPerformanceMetrics();
      pageViewUrl = window.location.href;
      sendEvent({
        event_type: 'page_view',
        page_url: window.location.href,
        page_title: document.title,
        active_time: activeTimeSeconds,
        ...perfMetrics,
      }, true);
      pageSequence++;
    },

    // Second pass at the performance marks, after window.load.
    //
    // trackPageView above deliberately fires immediately so a fast bounce in
    // the in-app browser still creates a session — but this is a deferred
    // script, so at that moment readyState is 'interactive' and both
    // loadEventEnd and domContentLoadedEventEnd are still 0. Both guards in
    // getPerformanceMetrics therefore evaluated false forever, which is why
    // page_load_time and dom_content_loaded were 100% NULL in production.
    //
    // Re-read them once the load event has actually been written and send
    // them on a HEARTBEAT, not a second page_view: a second page_view would
    // create a duplicate PageView row and a duplicate journey step, wrecking
    // bounce rate and pages/session. The server backfills these onto the
    // existing PageView only where they are still NULL (analytics/views.py).
    trackPagePerformance() {
      const perfMetrics = getPerformanceMetrics();
      // Nothing worth a round trip if every mark is still unusable (page was
      // backgrounded through load, or the browser has no Navigation Timing).
      const hasValue = Object.keys(perfMetrics)
        .some(key => perfMetrics[key] !== null && perfMetrics[key] !== undefined);
      if (!hasValue) return;
      pendingPerfMetrics = perfMetrics;
      perfResendsLeft = PERF_RESENDS;
      queueEvent({
        event_type: 'heartbeat',
        page_url: pageViewUrl || window.location.href,
        page_title: document.title,
        ...perfMetrics,
      });
    },

    // Track product view
    trackProductView(productId, productName, productPrice) {
      queueEvent({
        event_type: 'product_view',
        page_url: window.location.href,
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
      });
    },

    // How hard a visitor worked a drift rail. Arrow taps used to be 43% of
    // every click on the site and were our strongest engagement signal;
    // auto-drifting rails have no arrows, so dwell and drag distance stand in
    // their place. element_text carries the dragged pixels — how much the
    // reader drove the rail themselves rather than watching it drift.
    trackRailEngagement(railId, dwellMs, dragPx) {
      queueEvent({
        event_type: 'rail_engagement',
        page_url: window.location.href,
        element_id: railId,
        element_text: 'drag=' + Math.round(dragPx || 0),
        duration_ms: Math.round(dwellMs || 0),
      });
    },

    // Track product impressions (when products are visible in list)
    trackProductImpression(productId, productName, productPrice) {
      queueEvent({
        event_type: 'product_impression',
        page_url: window.location.href,
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
      });
    },

    // Track add to cart (also tags the cart for webhook attribution)
    trackAddToCart(productId, productName, productPrice, quantity = 1) {
      OllieTotAnalytics.tagCart();
      queueEvent({
        event_type: 'cart_update',
        element_id: 'cart:add',
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
        quantity: quantity,
      });
      sendEvent({
        event_type: 'add_to_cart',
        page_url: window.location.href,
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
        quantity: quantity,
      });
      const unit = oaiAmount(productPrice);
      const item = {
        id: String(productId || ''),
        name: productName || '',
        content_type: 'product',
        quantity: quantity,
      };
      if (unit !== null) { item.amount = unit; item.currency = 'USD'; }
      const cart = { type: 'contents', contents: [item] };
      if (unit !== null) { cart.amount = unit * quantity; cart.currency = 'USD'; }
      oaiMeasure('items_added', cart,
        'add-' + getSessionId() + '-' + Date.now());
    },

    // Track any cart mutation: action is 'add', 'increase', 'decrease', or
    // 'remove'; quantity is the line's NEW quantity after the change.
    trackCartUpdate(action, productId, productName, productPrice, quantity) {
      sendEvent({
        event_type: 'cart_update',
        element_id: 'cart:' + action,
        product_id: productId,
        product_name: productName,
        product_price: productPrice,
        quantity: quantity,
      });
    },

    // Track a newsletter/email capture submission (first-party data only).
    // Beaconed: the form posts and navigates on the same gesture, so this has
    // the same teardown race as checkout_start.
    trackEmailSignup(email, formId) {
      sendEvent({
        event_type: 'email_signup',
        element_id: formId || 'email-capture',
        element_text: email,
      }, true);
    },

    // Track cart view
    trackCartView(cartValue, itemCount) {
      sendEvent({
        event_type: 'cart_view',
        page_url: window.location.href,
      });
    },

    // Track cart abandonment (call when user leaves cart page)
    trackCartAbandon() {
      queueEvent({
        event_type: 'cart_abandon',
        page_url: window.location.href,
      });
    },

    // Track checkout steps
    trackCheckoutStep(stepNumber, stepName) {
      sendEvent({
        event_type: 'checkout_step',
        page_url: window.location.href,
        checkout_step: stepNumber,
        checkout_step_name: stepName,
      });
    },

    // Track checkout start (re-tags the cart in case it was built without JS)
    //
    // Two defects fixed here, both measured in production.
    //
    // Transport: this fires on a click that submits a form and navigates
    // straight to Shopify's hosted checkout. The keepalive fetch it used to
    // rely on is exactly what the comment above sendEvent warns against — it
    // is unreliable when the WKWebView is torn down mid-navigation, and the
    // in-app browser is the majority of traffic. sendBeacon survives that,
    // and sendBatch still falls back to fetch if the beacon is refused, so
    // there is no downside.
    //
    // Duplication: one production session emitted four checkout_start events
    // inside a single second. There are several ways to get there — a
    // double-tap on a slow phone, the click handler seeing both the button
    // and a nested element, the cart drawer and the cart page both mounting a
    // checkout button — and the funnel is read per session, so a burst
    // inflates nothing but it does make per-event counts meaningless.
    // Collapse a burst; a genuine second attempt after the navigation fails
    // happens on a fresh page, where this state no longer exists.
    trackCheckoutStart() {
      const now = Date.now();
      if (now - lastCheckoutStart < CHECKOUT_START_DEBOUNCE_MS) return;
      lastCheckoutStart = now;
      OllieTotAnalytics.tagCart();
      sendEvent({
        event_type: 'checkout_start',
        page_url: window.location.href,
      }, true);
      // Keyed on the debounced timestamp, so the burst this method already
      // collapses cannot become several checkout_started events on OpenAI's side.
      oaiMeasure('checkout_started', { type: 'contents' },
        'checkout-' + getSessionId() + '-' + now);
    },

    // Track checkout abandonment
    trackCheckoutAbandon(stepNumber) {
      queueEvent({
        event_type: 'checkout_abandon',
        page_url: window.location.href,
        checkout_step: stepNumber,
      });
    },

    // Track purchase/conversion
    trackPurchase(orderValue, products = []) {
      const conversionData = {
        session_id: getSessionId(),
        conversion_value: orderValue,
      };

      // Debug mode - log conversion instead of sending
      if (config.debug) {
        console.group('💰 OllieTot Analytics Conversion');
        console.log('Conversion Data:', conversionData);
        console.groupEnd();
      } else {
        // Mark session as converted
        fetch(config.conversionEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Analytics-Key': config.apiKey,
          },
          body: JSON.stringify(conversionData),
        }).catch(err => console.error('Conversion tracking error:', err));
      }

      // Also track as purchase event
      sendEvent({
        event_type: 'purchase',
        page_url: window.location.href,
      });
    },

    // Track form interactions
    trackFormStart(formId) {
      currentFormId = formId;
      formStartTime = Date.now();
      queueEvent({
        event_type: 'form_start',
        page_url: window.location.href,
        form_id: formId,
      });
    },

    trackFormFieldFocus(formId, fieldName) {
      queueEvent({
        event_type: 'form_field_focus',
        page_url: window.location.href,
        form_id: formId,
        field_name: fieldName,
      });
    },

    trackFormFieldError(formId, fieldName, errorMessage) {
      sendEvent({
        event_type: 'form_field_error',
        page_url: window.location.href,
        form_id: formId,
        field_name: fieldName,
        field_error: errorMessage,
      });
    },

    trackFormAbandon(formId) {
      queueEvent({
        event_type: 'form_abandon',
        page_url: window.location.href,
        form_id: formId,
      });
    },

    // Beaconed for the same reason as trackEmailSignup: a submit navigates.
    trackFormSubmit(formId) {
      sendEvent({
        event_type: 'form_submit',
        page_url: window.location.href,
        form_id: formId,
      }, true);
      currentFormId = null;
      formStartTime = null;
    },

    // Track search
    trackSearch(query, resultsCount) {
      sendEvent({
        event_type: resultsCount > 0 ? 'search' : 'search_no_results',
        page_url: window.location.href,
        search_query: query,
        search_results_count: resultsCount,
      });
    },

    // Track click events
    trackClick(elementId, elementClass, elementText) {
      queueEvent({
        event_type: 'click',
        page_url: window.location.href,
        element_id: elementId || '',
        element_class: elementClass || '',
        element_text: elementText || '',
      });
    },

    // Track scroll depth
    trackScroll(scrollDepth) {
      queueEvent({
        event_type: 'scroll',
        page_url: window.location.href,
        scroll_depth: scrollDepth,
        active_time: activeTimeSeconds,
      });
    },
  };

  // Auto-tracking setup
  function setupAutoTracking() {
    // Fire the page view immediately. Waiting for window.load lost the entire
    // landing session on fast bounces in the Instagram in-app browser (the
    // dominant traffic source), because the user tapped away before load
    // fired. Performance metrics that depend on window.load are best-effort
    // and may be null when captured this early — an acceptable trade for never
    // losing the session itself.
    OllieTotAnalytics.trackPageView();

    // ...then come back for the load-dependent performance marks once they
    // exist. setTimeout(0) rather than the load handler itself: loadEventEnd
    // is written when the load event finishes dispatching, so reading it from
    // inside a load listener still returns 0. Yielding one task fixes that.
    const readPerformanceAfterLoad = () => {
      setTimeout(() => OllieTotAnalytics.trackPagePerformance(), 0);
    };
    if (document.readyState === 'complete') {
      readPerformanceAfterLoad();
    } else {
      window.addEventListener('load', readPerformanceAfterLoad, { once: true });
    }

    // Start active time tracking
    trackActiveTime();

    // Start heartbeat tracking
    startHeartbeat();

    // Track user activity
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'].forEach(event => {
      document.addEventListener(event, recordActivity, { passive: true });
    });

    // Track scroll depth
    let maxScroll = 0;
    let scrollTimer = null;
    const scrollMilestones = [1, 25, 50, 75, 90, 99];
    const firedMilestones = new Set();

    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;

        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          // Fire events for any milestones we've crossed but haven't fired yet
          scrollMilestones.forEach(milestone => {
            if (maxScroll >= milestone && !firedMilestones.has(milestone)) {
              firedMilestones.add(milestone);
              OllieTotAnalytics.trackScroll(milestone);
            }
          });
        }, 1000);
      }
    }, { passive: true });

    // Track outbound clicks
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a, button');
      if (target) {
        OllieTotAnalytics.trackClick(
          resolveElementId(target),
          target.className,
          target.textContent.trim().substring(0, 100)
        );
      }
    });

    // Track hover dwell on interactive elements and images (mouse only —
    // touch devices emit synthetic hover noise). An element counts once the
    // pointer has rested on it for hoverMinMs; the event carries total dwell.
    const HOVER_SELECTOR = 'a, button, img, [data-analytics-id]';
    let hoverEl = null;
    let hoverStart = 0;

    function finishHover() {
      if (!hoverEl) return;
      const dwell = Date.now() - hoverStart;
      if (dwell >= config.hoverMinMs) {
        queueEvent({
          event_type: 'hover',
          page_url: window.location.href,
          element_id: resolveElementId(hoverEl),
          element_class: typeof hoverEl.className === 'string' ? hoverEl.className : '',
          element_text: (hoverEl.textContent || hoverEl.alt || '').trim().substring(0, 100),
          duration_ms: dwell,
        });
      }
      hoverEl = null;
    }

    document.addEventListener('pointerover', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const target = e.target.closest ? e.target.closest(HOVER_SELECTOR) : null;
      if (target === hoverEl) return;
      finishHover();
      if (target) {
        hoverEl = target;
        hoverStart = Date.now();
      }
    }, { passive: true });

    document.addEventListener('pointerout', (e) => {
      if (!hoverEl) return;
      // Only close out when the pointer actually leaves the hovered element
      if (!e.relatedTarget || !hoverEl.contains(e.relatedTarget)) {
        finishHover();
      }
    }, { passive: true });

    // Auto-track forms
    document.addEventListener('focus', (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        const form = e.target.closest('form');
        if (form) {
          const formId = form.id || form.name || 'unnamed-form';
          if (currentFormId !== formId) {
            OllieTotAnalytics.trackFormStart(formId);
          }
          OllieTotAnalytics.trackFormFieldFocus(formId, e.target.name || e.target.id || 'unnamed-field');
        }
      }
    }, true);

    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.tagName === 'FORM') {
        const formId = form.id || form.name || 'unnamed-form';
        OllieTotAnalytics.trackFormSubmit(formId);
      }
    }, true);

    // Detect cart abandonment (if on cart page and navigating away)
    if (window.location.pathname.includes('/cart')) {
      window.addEventListener('beforeunload', () => {
        if (!window.location.pathname.includes('/checkout')) {
          OllieTotAnalytics.trackCartAbandon();
        }
      });
    }

    // Detect form abandonment
    window.addEventListener('beforeunload', () => {
      if (currentFormId && formStartTime && (Date.now() - formStartTime > 5000)) {
        OllieTotAnalytics.trackFormAbandon(currentFormId);
      }
    });

    // Unload-time delivery is consolidated in finalFlush(), registered at the
    // end of setup once hover and image tracking exist.

    // Track product impressions on scroll (for product lists)
    const observeProductImpressions = () => {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const productEl = entry.target;
              const productId = productEl.dataset.productId;
              const productName = productEl.dataset.productName;
              const productPrice = productEl.dataset.productPrice;

              if (productId && !productEl.dataset.tracked) {
                OllieTotAnalytics.trackProductImpression(productId, productName, productPrice);
                productEl.dataset.tracked = 'true';
                observer.unobserve(productEl);
              }
            }
          });
        }, { threshold: 0.5 });

        // Observe all products with data-product-id attribute. Buy-box forms
        // carry the same attributes for add-to-cart tracking but are the page's
        // subject, not an impression — the PDP fires product_view instead.
        document.querySelectorAll('[data-product-id]:not([data-product-form])').forEach(el => {
          observer.observe(el);
        });
      }
    };

    // Set up product impression tracking after DOM loads
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeProductImpressions);
    } else {
      observeProductImpressions();
    }

    // Buy boxes reached in-page count as product views.
    //
    // The homepage's "Shop the Purse" buttons used to load a PDP, and the PDP
    // is what fires product_view — 77 sessions' worth. Pointing those buttons
    // at an anchor on the same page removes the page load, and would silently
    // remove the event with it, taking the mid-funnel metric and the Meta CAPI
    // signal along. So a buy box that comes properly into view off the PDP
    // fires product_view itself: once per product per page view, at a higher
    // visibility bar than an impression, because reaching a buy box is a
    // stronger act than a photo scrolling past.
    const observeInPageProductViews = () => {
      if (!('IntersectionObserver' in window)) return;
      // On a product page the section's own trackProductView call is the
      // single source; do not double-count it here.
      if (/\/products\//.test(window.location.pathname)) return;
      const seen = new Set();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          // Not intersectionRatio: a buy box is often taller than the phone
          // viewport, and an element bigger than the screen can never be 50%
          // visible, so the ratio test silently never fired for the second
          // product. Measure against whichever is smaller — the element or
          // the viewport — which is what "half of it is on screen" means.
          const box = entry.target;
          const reference = Math.min(entry.boundingClientRect.height,
                                     window.innerHeight) || 1;
          if (entry.intersectionRect.height / reference < 0.5) return;
          const id = box.dataset.productId;
          if (!id || seen.has(id)) return;
          seen.add(id);
          const btn = box.querySelector('[data-add-btn]');
          const active = box.querySelector('.swatch.is-active');
          const cents = Number((active && active.dataset.price) || (btn && btn.dataset.price));
          OllieTotAnalytics.trackProductView(
            id, box.dataset.productName, isNaN(cents) ? undefined : cents / 100);
          observer.unobserve(box);
        });
      }, { threshold: [0, 0.15, 0.3, 0.5, 0.75, 1] });
      document.querySelectorAll('[data-product-form][data-product-id]').forEach(el => {
        observer.observe(el);
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeInPageProductViews);
    } else {
      observeInPageProductViews();
    }

    // Image impression tracking: an image ≥30% on screen starts a visibility
    // timer; when it drops below the threshold (or the page hides) an
    // image_impression event fires with the visible duration, a slug derived
    // from the filename, and the image URL. Covers <img> tags and the theme's
    // inline background-image elements. Elements tagged [data-review-id]
    // (testimonial cards) and [data-price-impression] (any displayed price)
    // ride the same machinery and fire review_impression / price_impression.
    const imageEpisodes = new Map();  // element -> visibility start (ms)

    function endImageEpisode(el) {
      const start = imageEpisodes.get(el);
      if (start === undefined) return;
      imageEpisodes.delete(el);
      const visibleMs = Date.now() - start;
      if (visibleMs < config.imageMinVisibleMs) return;
      // Checked at episode end, not start: reveal-animated elements begin at
      // opacity 0 and fade in without a new intersection event, so an
      // entry-time check would skip them forever. The stacked crossfade
      // renders that stay invisible get dropped here instead.
      const style = window.getComputedStyle(el);
      if (style.opacity === '0' || style.visibility === 'hidden') return;
      // A `position: fixed` layer's containing block is the viewport, and
      // IntersectionObserver honours an ancestor's OVERFLOW clipping but not
      // its clip-path — so `.hero--parallax .hero-media` (fixed; inset: 0
      // above 640px, contained visually by the hero's clip-path) never stops
      // intersecting once it starts. Its episode is closed only by
      // closeAllImageEpisodes at pagehide, which would report the whole page
      // dwell as "how long this image was on screen": one such row per
      // desktop session, in the exact column where a single 2.0-hour session
      // once held 47.9% of all measured dwell. Emit the impression — it is
      // true that the hero was seen — with no duration rather than a wrong
      // one. undefined is dropped by JSON.stringify, so the field simply
      // does not arrive; the server also nulls anything past 30 minutes, for
      // the copies of this file already cached in the wild.
      const measuredMs = style.position === 'fixed' ? undefined : visibleMs;
      // Price impressions ride the same visibility machinery as images and
      // reviews: which price a visitor actually saw, where on the page, how
      // far down, and for how long. Before this existed the first price on
      // the homepage sat 4.4 screenfuls down and reached under a tenth of
      // mobile visitors, which made every read of "is $38 too much?"
      // unanswerable — the question was never being asked of most of them.
      if (el.hasAttribute('data-price-impression')) {
        const form = el.closest('[data-product-form]');
        const priceText = (el.textContent || '').trim();
        const raw = el.getAttribute('data-price-value');
        const parsed = raw !== null && raw !== ''
          ? parseFloat(raw)
          : parseFloat((priceText.match(/\$\s*([\d,]+(?:\.\d{1,2})?)/) || [])[1] || '');
        queueEvent({
          event_type: 'price_impression',
          page_url: window.location.href,
          element_id: el.getAttribute('data-price-placement')
            || resolveElementId(el)
            || 'price:unknown',
          element_class: typeof el.className === 'string' ? el.className : '',
          element_text: priceText.slice(0, 120),
          product_price: isNaN(parsed) ? undefined : parsed,
          product_id: el.getAttribute('data-product-id')
            || (form ? form.dataset.productId : undefined),
          product_name: el.getAttribute('data-product-name')
            || (form ? form.dataset.productName : undefined),
          duration_ms: measuredMs,
        });
        return;
      }
      if (el.hasAttribute('data-review-id')) {
        const who = el.querySelector('.who');
        queueEvent({
          event_type: 'review_impression',
          page_url: window.location.href,
          element_id: resolveElementId(el) || 'review:' + el.getAttribute('data-review-id'),
          element_class: typeof el.className === 'string' ? el.className : '',
          element_text: who ? who.textContent.trim() : '',
          duration_ms: measuredMs,
        });
        return;
      }
      let url = imageUrlOf(el, style);
      if (!url) return;
      // Inline styles carry URLs as authored — often protocol-relative from
      // Shopify's asset_url. Resolve to absolute; skip inline data/blob URIs.
      try {
        url = new URL(url, window.location.href).href;
      } catch (err) {
        return;
      }
      if (url.startsWith('data:') || url.startsWith('blob:')) return;
      // An element that carries its OWN image identifies itself by that
      // image, never by a clickable ancestor's data-analytics-id. Without
      // this, tagging <a class="tile"> so the collection tiles stop sending
      // an empty element_id on click would ALSO re-key their impressions
      // from `img:ot-tile-paloma` to `collection-tile:the-paloma`, mid-flight
      // and mid-measurement-window — and any query joining pre- and
      // post-deploy rows on element_id would break across the boundary.
      // <img> keeps resolveElementId's ancestor prefix, which is how the
      // photo rails have always been keyed (photo-rail:paloma:…:img:…).
      const ownTag = el.getAttribute('data-analytics-id');
      const slugId = 'img:' + (imageSlugOf(url) || 'unknown');
      queueEvent({
        event_type: 'image_impression',
        page_url: window.location.href,
        element_id: el.tagName === 'IMG'
          ? (resolveElementId(el) || slugId)
          : (ownTag || slugId),
        element_class: typeof el.className === 'string' ? el.className : '',
        image_url: url,
        duration_ms: measuredMs,
      });
    }

    let closeAllImageEpisodes = () => {};

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const visible = entry.isIntersecting
            && entry.intersectionRatio >= config.imageVisibleThreshold;
          if (visible && !imageEpisodes.has(entry.target)) {
            imageEpisodes.set(entry.target, Date.now());
          } else if (!visible) {
            endImageEpisode(entry.target);
          }
        });
      }, { threshold: [config.imageVisibleThreshold] });

      const observedImages = new WeakSet();
      const observeImages = () => {
        // [data-drift-clone] marks the duplicated half of a drift rail's
        // track, which exists only so the loop wraps seamlessly. Counting
        // it would double every rail impression.
        //
        // [style*="url("] is the wider net that catches the hero, whose
        // inline style declares CSS CUSTOM PROPERTIES (--hero-wide /
        // --hero-portrait) rather than background-image itself — so the
        // background-image substring test never matched it and it was never
        // even observed. imageUrlOf reads the resolved value. The narrower
        // background-image selector is kept for any element that sets it
        // inline from a var() with no literal url( in the attribute.
        document.querySelectorAll('img:not([data-drift-clone]), [style*="background-image"]:not([data-drift-clone]), [style*="url("]:not([data-drift-clone]), [data-review-id], [data-price-impression]').forEach(el => {
          if (observedImages.has(el)) return;
          observedImages.add(el);
          imageObserver.observe(el);
        });
      };
      observeImages();
      new MutationObserver(observeImages)
        .observe(document.body, { childList: true, subtree: true });

      closeAllImageEpisodes = () => {
        Array.from(imageEpisodes.keys()).forEach(endImageEpisode);
      };
    }

    // Final flush: when the page hides or unloads, close out everything still
    // in flight — the open hover, all visible-image episodes, and a last
    // heartbeat snapshot (current scroll depth, time on page, active time) so
    // engagement data survives sessions that end by closing the tab. The
    // batch goes out via sendBeacon, the only delivery that works mid-unload.
    let lastFinalFlush = 0;
    function finalFlush() {
      const now = Date.now();
      if (now - lastFinalFlush < 1000) return;  // pagehide + beforeunload both fire
      lastFinalFlush = now;
      finishHover();
      closeAllImageEpisodes();
      queueEvent(withPendingPerf({
        event_type: 'heartbeat',
        page_url: window.location.href,
        page_title: document.title,
        scroll_depth: getCurrentScrollDepth(),
        active_time: activeTimeSeconds,
        time_on_page: Math.floor((now - pageLoadTime) / 1000),
      }));
      flushQueue(true);
    }
    window.addEventListener('pagehide', finalFlush);
    window.addEventListener('beforeunload', finalFlush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') finalFlush();
    });
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAutoTracking);
  } else {
    setupAutoTracking();
  }

  // Debug mode notification
  if (config.debug) {
    console.log(
      '%c🎯 OllieTot Analytics Debug Mode Enabled',
      'background: #4CAF50; color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold;'
    );
    console.log('Events will be logged to console instead of sent to server.');
    console.log('Set config.debug = false to enable server tracking.');
  }

  // Expose to window
  window.OllieTotAnalytics = OllieTotAnalytics;

  // Drain the pre-load command queue: inline Liquid snippets run before this
  // (deferred) script, so they push ['methodName', ...args] onto window.otaq.
  function runCommand(command) {
    const method = command[0];
    if (typeof OllieTotAnalytics[method] === 'function') {
      OllieTotAnalytics[method].apply(OllieTotAnalytics, command.slice(1));
    }
  }
  const pending = window.otaq;
  if (Array.isArray(pending)) {
    pending.forEach(runCommand);
  }
  window.otaq = { push: runCommand };

})();
